import React, { useContext } from "react";
import PropTypes from "prop-types";
import {
  BriefcaseBusiness,
  MapPin,
  SquareArrowOutUpRight,
  UserCheck,
} from "lucide-react";

import {
  MATCHER_LABEL,
  formatMatcher,
  pickDisplayRate,
  prepareHourlyRate,
  prepareLocation,
  getCurrentRole,
} from "components/UserCardList/helpers/userCard";
import { UpdateRecencyBadge } from "components/UserCardList/components/UpdateRecencyBadge";

import { ModalContext, SearchContext } from "context/providers";

import CountryFlag from "components/CountryFlag";
import UserActionIcons from "./UserActionIcons";
import HelpText from "components/HelpText";
import { PROFILE_META_LINE_CLASSNAME } from "components/UserCardList/components/UserCardResult/constants";

import MatchStatusHistoryInfo from "./MatchStatusHistoryInfo";
import Referrer from "./Referrer";
import Status from "./Status";
import SvgIcon from "components/base/SvgIcon";
import diceComLogo from "images/dice-com-logo.png";
import { cn } from "utils";
import { APPLICATION_TAB_NAMES } from "lookup";
import { CTA_TITLES } from "components/UserCardList/constants/ctaTitles";
import { getPlatformProfileUrl, getUserName } from "utils/helpers/users";

const UserInfo = ({
  hit,
  collectionKey,
  activeTabName,
  hideActionIcons = false,
  showProfileActions,
  actions,
}) => {
  const { showModal } = useContext(ModalContext);
  const { updateHit } = useContext(SearchContext);
  const currentRole = getCurrentRole(hit);

  const displayName = `${hit.given_name} ${hit.family_name}`;
  const locationText = prepareLocation(hit.location) || hit.locale;
  const primaryRole = currentRole;
  const matchStatus = hit.match?.applicationId
    ? hit.match?.status
    : hit.associatedMatch?.status;
  const displayRate = pickDisplayRate(hit);
  const showMatcher = activeTabName === APPLICATION_TAB_NAMES.MATCHED;
  const username = getUserName(hit);

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="mb-2 w-full">
        <div className="flex flex-wrap gap-4 lg:flex-nowrap lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-col gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h2
                  className="m-0 min-w-0 break-words text-3xl font-bold text-text-grey-900"
                  data-testid="candidate-name"
                >
                  {displayName}
                </h2>
                {hit.hasActivePlacements ? (
                  <SvgIcon
                    type="doNotDisturb"
                    title="User has active engagement(s)"
                  />
                ) : null}
              </div>
              <UpdateRecencyBadge
                hit={hit}
                isClickable={!!(hit.lastActivityDate || hit.createdAt)}
              />
            </div>

            <div
              className={cn(
                "mt-4 flex flex-wrap items-center gap-x-5 gap-y-2",
                PROFILE_META_LINE_CLASSNAME
              )}
            >
              {primaryRole && (
                <span className="inline-flex min-w-0 items-center gap-2">
                  <BriefcaseBusiness className="h-5 w-5 shrink-0 text-gray-600" />
                  <span className="min-w-0 break-words">{primaryRole}</span>
                </span>
              )}
              {locationText && (
                <span className="inline-flex min-w-0 items-center gap-2">
                  {hit.location?.countryCode?.toLowerCase() ? (
                    <CountryFlag
                      className="h-5 w-5 shrink-0 rounded-full"
                      country={hit.location.countryCode.toLowerCase()}
                      size="24x18"
                    />
                  ) : (
                    <MapPin className="h-5 w-5 shrink-0 text-gray-600" />
                  )}
                  <span className="min-w-0 break-words">{locationText}</span>
                </span>
              )}
              {displayRate && <span>{prepareHourlyRate(displayRate)}</span>}
              <Status matchStatus={matchStatus} variant="inline" />
              {showMatcher && (
                <span
                  className="inline-flex min-w-0 items-center gap-2"
                  title="Recruiter who matched this talent to the job"
                >
                  <UserCheck className="h-5 w-5 shrink-0 text-gray-600" />
                  <span className="min-w-0 break-words">
                    {MATCHER_LABEL}: {formatMatcher(hit.match?.matcher)}
                  </span>
                </span>
              )}
              {hit.talentSource === "dice-integration" && (
                <span
                  className="inline-flex items-center gap-2 text-[#CE2127]"
                  title="Talent sourced from Dice"
                >
                  <img
                    src={diceComLogo}
                    alt=""
                    className="h-5 w-5 rounded-full"
                  />
                  Profile from Dice
                </span>
              )}
              {hit.isUnclaimed && (
                <span
                  className="inline-flex items-center gap-2 text-gray-500"
                  title="User has not yet claimed this account"
                >
                  <SvgIcon type="personNotClaimed" className="h-5 w-5" />
                  Unclaimed profile
                </span>
              )}
            </div>
            {(username || hit.referrerCode) && (
              <div
                className={cn(
                  "mt-2 flex flex-wrap items-center gap-x-5 gap-y-2",
                  PROFILE_META_LINE_CLASSNAME
                )}
              >
                {username && (
                  <a
                    className="inline-flex min-w-0 items-center gap-1 font-bold text-sky-500 opacity-70 transition-all hover:underline hover:opacity-100"
                    href={getPlatformProfileUrl(username)}
                    target="_blank"
                    rel="noreferrer"
                    title={CTA_TITLES.openPlatformProfile}
                  >
                    <span className="min-w-0 break-all">@{username}</span>
                    <SquareArrowOutUpRight className="h-3 w-3 shrink-0" />
                  </a>
                )}
                <Referrer referralCode={hit.referrerCode} />
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-col items-end gap-2 lg:flex-none">
            {actions}
            {!actions && showProfileActions ? (
              <UserActionIcons
                hit={hit}
                collectionKey={collectionKey}
                activeTabName={activeTabName}
                updateHit={updateHit}
                showModal={showModal}
              />
            ) : null}
            {hit?.reasonsForMatch && (
              <div className="flex justify-end gap-4 mr-2">
                <HelpText
                  iconType="text"
                  iconText="Why Fit?"
                  className="flex justify-start"
                  iconTextClassName="whitespace-nowrap  !text-sky-500"
                >
                  <ul className="max-w-md space-y-1 text-black font-inter text-normal list-disc list-inside normal-case">
                    {hit.reasonsForMatch.map((el) => {
                      return (
                        <li key={el} className="font-inter font-normal text-sm">
                          {el}
                        </li>
                      );
                    })}
                  </ul>
                </HelpText>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 pl-2 mt-2">
          <MatchStatusHistoryInfo
            hit={hit}
            collectionKey={collectionKey}
            configStatusFields={{
              SKIPPED: {
                title: "Previously Skipped",
                iconType: "cancel",
                hitFieldName: "previouslySkipped",
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

UserInfo.propTypes = {
  hit: PropTypes.object,
  collectionKey: PropTypes.string,
  activeTabName: PropTypes.string,
  hideActionIcons: PropTypes.bool,
  showProfileActions: PropTypes.bool,
  actions: PropTypes.node,
};

UserInfo.defaultProps = {
  hit: {},
  collectionKey: "hits",
  activeTabName: "SEARCHRESULTS",
  hideActionIcons: false,
  showProfileActions: true,
  actions: null,
};

export default UserInfo;
